import { TerraformVariableSchema, TerraformType } from '../types';

/**
 * Parse inline comments from a Terraform type definition string.
 *
 * Terraform type definitions can include inline comments after field definitions:
 * ```
 * object({
 *   enable = optional(bool, false) # Whether to enable the feature
 *   location = optional(string, "us-central1") # GCP region
 * })
 * ```
 *
 * This function extracts these comments and maps them to field names.
 *
 * @param rawType The raw type definition string from Terraform
 * @returns A map of field names to their inline comments
 */
export function parseTypeComments(
  rawType: string | undefined,
): Record<string, string> {
  if (!rawType) {
    return {};
  }

  const comments: Record<string, string> = {};

  // Match patterns like: field_name = ... # comment
  // The regex captures:
  // 1. field_name (word characters before =)
  // 2. comment (everything after # until end of line)
  const lines = rawType.split('\n');

  for (const line of lines) {
    // Match: field_name = anything # comment
    const match = line.match(/^\s*(\w+)\s*=\s*[^#]*#\s*(.+?)\s*$/);
    if (match) {
      const [, fieldName, comment] = match;
      comments[fieldName] = comment.trim();
    }
  }

  return comments;
}

/**
 * Generate a default value for a given Terraform type
 */
export function getDefaultForType(
  type: TerraformType,
  elementSchema?: TerraformVariableSchema,
): unknown {
  switch (type) {
    case 'string':
      return '';
    case 'number':
      return 0;
    case 'bool':
      return false;
    case 'object':
      if (elementSchema?.objectSchema) {
        const obj: Record<string, unknown> = {};
        for (const [key, schema] of Object.entries(
          elementSchema.objectSchema,
        )) {
          obj[key] = getDefaultForType(schema.type, schema.elementSchema);
        }
        return obj;
      }
      return {};
    case 'list':
    case 'set':
      return [];
    case 'map':
      return {};
    case 'tuple':
      return [];
    default:
      return '';
  }
}

/**
 * Deep clone an object for duplication
 */
export function deepClone<T>(value: T): T {
  if (value === null || value === undefined) {
    return value;
  }
  return JSON.parse(JSON.stringify(value));
}

/**
 * Generate a summary string for an object value (for collapsed headers)
 */
export function getObjectSummary(
  value: Record<string, unknown>,
  maxFields: number = 3,
): string {
  if (!value || typeof value !== 'object') {
    return '';
  }

  const entries = Object.entries(value);
  if (entries.length === 0) {
    return '(empty)';
  }

  const summary = entries
    .slice(0, maxFields)
    .map(([key, val]) => {
      const displayVal =
        typeof val === 'string'
          ? `"${val.slice(0, 20)}${val.length > 20 ? '...' : ''}"`
          : typeof val === 'number' || typeof val === 'boolean'
            ? String(val)
            : Array.isArray(val)
              ? `[${val.length}]`
              : typeof val === 'object' && val !== null
                ? '{...}'
                : String(val);
      return `${key}: ${displayVal}`;
    })
    .join(', ');

  const remaining = entries.length - maxFields;
  return remaining > 0 ? `${summary}, +${remaining} more` : summary;
}

/**
 * Generate a unique key for map entries
 */
export function generateUniqueKey(
  existingKeys: string[],
  baseKey: string = 'key',
): string {
  let key = baseKey;
  let counter = 1;
  while (existingKeys.includes(key)) {
    key = `${baseKey}_${counter}`;
    counter++;
  }
  return key;
}
